import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, api, errorMessage } from '../api';
import { useCall } from '../call/CallContext';
import { disablePush, enablePush, registerPwa } from '../pwa';
import type {
  BackupSummary, BudgetPolicy, Device, Initiative, InitiativeSettings, MemoryItem, Operation,
  MemoryRecord, Message, Recording, TrashItem, UsageSummary
} from '../types';
import { formatBytes, formatDate, formatDuration } from '../format';
import { capabilityPresentation, readinessLabel } from '../status';
import { Button, Empty, ErrorNotice, Meter, Panel, Skeleton, Status } from '../ui';
import { PersonaControlPanels } from './persona-extras';
import { ProviderActivationPanels } from './providers';

function useLoad<T>(loader: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await loader()); } catch (caught) { setError(errorMessage(caught)); } finally { setLoading(false); }
  }, [loader]);
  useEffect(() => { void load(); }, [load]);
  return { data, error, loading, reload: load, setData };
}

const STEP_LABELS: Record<string, string> = {
  barrier: 'إيقاف استخدام المحتوى',
  sources: 'إزالة المصادر والمشتقات',
  derivatives: 'إزالة المشتقات والروابط والمتجهات',
  tombstones: 'منع الاستعادة من النسخ القديمة',
  cache: 'تحديث البحث والذاكرة المؤقتة',
  restore: 'استرجاع العناصر'
};

const TRUTH_LABELS: Record<string, string> = {
  canonical: 'حقيقة ثابتة معتمدة',
  user_authored_fictional: 'خلفية قصصية من تأليف المالك',
  observed_event: 'حدث وقع فعلًا داخل رامي',
  user_confirmed: 'معلومة أكدها فارس',
  derived: 'استنتاج مشتق قابل للمراجعة',
  uncertain: 'غير مؤكدة',
  superseded: 'استبدلتها نسخة أحدث',
  invalidated: 'أُبطلت بتصحيح أو حذف'
};

const STATUS_LABELS: Record<string, string> = {
  proposed: 'مقترح',
  pending_validation: 'قيد المراجعة',
  needs_clarification: 'يحتاج توضيحًا',
  approved: 'معتمد',
  rejected: 'مرفوض',
  superseded: 'مستبدَل',
  invalidated: 'مُبطَل',
  uploading: 'قيد الرفع',
  incomplete: 'غير مكتمل',
  complete: 'مكتمل',
  deleted: 'محذوف',
  retained: 'محتفظ به',
  blocked: 'بانتظار التأكيد',
  running: 'قيد التنفيذ',
  locally_hidden: 'محجوب محليًا',
  tombstone_confirmed: 'المنع مثبت',
  trashed: 'في السلة',
  restored: 'مُسترجَع',
  failed: 'فشل',
  pending: 'معلّق'
};

function statusLabel(value: string): string {
  return STATUS_LABELS[value] ?? value;
}

function stepLabel(id: string): string {
  return STEP_LABELS[id] ?? 'خطوة غير معروفة';
}

function describeSteps(request: { steps?: Array<Record<string, unknown>>; mode?: string }): string {
  const steps = request.steps ?? [];
  if (steps.length === 0) return 'اكتملت العملية.';
  return 'الخطوات: ' + steps.map((step) => stepLabel(String(step['step'] ?? ''))).join(' ← ') + '.';
}

function describePermanent(preview: { preview?: Record<string, unknown>; targetIds?: string[] }): string {
  const details = preview.preview ?? {};
  const messages = (details['messages'] as Array<{ derivedCount?: number; candidateCount?: number }> | undefined) ?? [];
  const conversations = (details['conversations'] as Array<{ messageCount?: number; derivedCount?: number }> | undefined) ?? [];
  const memories = (details['memories'] as Array<{ sourceCount?: number; embeddingCount?: number }> | undefined) ?? [];
  const lines = ['هذا حذف نهائي لا يمكن التراجع عنه.'];
  if (memories.length) lines.push('المصادر المرتبطة: ' + String(memories[0]?.sourceCount ?? 0) + ' — المتجهات: ' + String(memories[0]?.embeddingCount ?? 0));
  if (messages.length) lines.push('المشتقات الحصرية: ' + String(messages[0]?.derivedCount ?? 0) + ' — المرشحات: ' + String(messages[0]?.candidateCount ?? 0));
  if (conversations.length) lines.push('الرسائل: ' + String(conversations[0]?.messageCount ?? 0));
  lines.push('سيُنشأ سجل منع استعادة (Tombstone) بدون نص المحتوى.');
  return lines.join('\n');
}

function PageTitle({ title, children }: { title: string; children: ReactNode }) {
  return <header className="page-title"><p className="eyebrow">لوحة المالك</p><h1>{title}</h1><p>{children}</p></header>;
}

export function OverviewPage() {
  const loader = useCallback(async () => {
    const [ready, status] = await Promise.all([
      api.request<{ status: 'ready' | 'not_ready'; checks?: Record<string, boolean> }>('/health/ready'),
      api.ownerStatus()
    ]);
    return { ready, status };
  }, []);
  const { data, error, loading, reload } = useLoad(loader);
  return <><PageTitle title="النظرة العامة">حالة رامي الفعلية كما تعيدها الخدمات، دون تقدير واجهي.</PageTitle>
    {error ? <ErrorNotice message={error} onRetry={() => void reload()} /> : null}
    {loading ? <Skeleton lines={6} /> : data ? <div className="dashboard-grid">
      <Panel title="جاهزية النسخة الأساسية"><Status tone={data.ready.status === 'ready' ? 'good' : 'bad'}>{data.ready.status === 'ready' ? 'الخدمة الأساسية جاهزة' : 'تحتاج تدخلًا'}</Status><div className="status-list" aria-label="فحوص الجاهزية">{Object.entries(data.ready.checks ?? {}).map(([name, ok]) => <div className="status-item" key={name}><span>{readinessLabel(name)}</span><Status tone={ok ? 'good' : 'bad'}>{ok ? 'سليم' : 'متعطل'}</Status></div>)}</div></Panel>
      <Panel title="حالة القدرات"><div className="status-list capability-list" aria-label="حالة القدرات">{Object.entries(data.status.capabilities).map(([name, value]) => { const presentation = capabilityPresentation(name, value); return <div className="status-item capability-item" key={name} dir="rtl"><span className="capability-label">{presentation.label}</span><Status tone={presentation.tone}>{presentation.message}</Status></div>; })}</div></Panel>
      <Panel title="حدود النسخة المنشورة"><p>لوحات المزودات والنسخ والشخصية والأجهزة متاحة في التنقل وتقرأ حالتها من الـAPI المنشور. القدرات التي تنتظر اعتمادًا حقيقيًا تُعرض «غير مفعّلة» ولا تُعرض ناجحة.</p><p className="muted">يتحقق مدير الخدمات من العامل بصورة مستقلة؛ لا تدعي هذه الصفحة فحصه من داخل API.</p></Panel>
    </div> : null}
  </>;
}

const PERSONA_TEXT_FIELDS: Array<[string, string]> = [
  ['identity.displayNameFinal', 'الاسم المعروض'],
  ['identity.temperament', 'الطبع'],
  ['identity.warmth', 'درجة الود'],
  ['identity.humorStyle', 'أسلوب المزاح'],
  ['communication.tone', 'النبرة'],
  ['communication.dialect', 'اللهجة'],
  ['communication.directness', 'المباشرة'],
  ['communication.honestyStyle', 'أسلوب الصراحة'],
  ['communication.apologyStyle', 'أسلوب الاعتذار'],
  ['communication.privacy', 'الخصوصية'],
  ['communication.humor', 'المزاح'],
  ['communication.care', 'التعبير عن الاهتمام'],
  ['profile.short', 'تعريف مختصر'],
  ['profile.extended', 'تعريف موسع'],
  ['profile.role', 'الدور'],
  ['boundaries.truthfulness', 'أسلوب الصدق وحدود المعرفة'],
  ['story.birthDate', 'تاريخ الميلاد (سردي)'],
  ['story.birthPlace', 'مكان النشأة'],
  ['story.residence', 'مكان الإقامة'],
  ['story.timeZone', 'المنطقة الزمنية'],
  ['story.travelNotes', 'ملاحظات السفر'],
  ['story.ageProgression', 'تقدم العمر'],
  ['biography.summaryShort', 'نبذة مختصرة'],
  ['biography.summaryExtended', 'نبذة موسعة'],
  ['biography.role', 'الدور الحياتي'],
  ['biography.currentCircumstances', 'الظروف الحالية'],
  ['biography.plans', 'الخطط القادمة']
];

function nested(value: Record<string, unknown> | null | undefined, path: string): string {
  let cursor: unknown = value ?? {};
  for (const part of path.split('.')) {
    if (typeof cursor !== 'object' || cursor === null) return '';
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return typeof cursor === 'string' ? cursor : '';
}

export function PersonaPage() {
  const loader = useCallback(() => api.personas().then(async (page) => {
    const persona = page.items[0];
    if (!persona) return { persona: null, profile: null, context: null };
    const [profile, context] = await Promise.all([
      api.personaProfile(persona.id),
      api.personaContextPreview(persona.id, { channel: 'text' }).catch(() => null)
    ]);
    return { persona, profile, context };
  }), []);
  const { data, error, loading, reload } = useLoad(loader);
  const persona = data?.persona ?? null;
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [storyEnabled, setStoryEnabled] = useState(false);
  const [storyItems, setStoryItems] = useState<Array<{ key: string; label: string; value: string }>>([]);
  const [domainRows, setDomainRows] = useState<Array<{ key: string; label: string; level: string; state: string; note: string }>>([]);
  const document = (data?.profile?.persona ?? null) as unknown as Record<string, unknown> | null;

  useEffect(() => {
    const story = (document?.['fictionalBackstory'] ?? null) as { enabled?: boolean; items?: Array<{ key?: string; label?: string; value?: string }> } | null;
    setStoryEnabled(story?.enabled === true);
    setStoryItems((story?.items ?? []).map((item, index) => ({
      key: typeof item.key === 'string' && item.key ? item.key : 'story_' + String(index + 1),
      label: String(item.label ?? ''),
      value: String(item.value ?? '')
    })));
    const knowledge = (document?.['knowledge'] ?? null) as { domains?: Array<{ key?: string; label?: string; level?: string; state?: string; note?: string }> } | null;
    setDomainRows((knowledge?.domains ?? []).map((domain, index) => ({
      key: typeof domain.key === 'string' && domain.key ? domain.key : 'domain_' + String(index + 1),
      label: String(domain.label ?? ''),
      level: String(domain.level ?? 'operational'),
      state: String(domain.state ?? 'awaiting_provider'),
      note: String(domain.note ?? '')
    })));
  }, [persona]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!persona) return;
    setSaving(true); setNotice('');
    try {
      const fields: Record<string, unknown> = {};
      for (const [path] of PERSONA_TEXT_FIELDS) {
        const next = draft[path];
        if (next !== undefined) fields[path] = next;
      }
      const form = new FormData(event.currentTarget);
      const toggled = String(form.get('storyEnabled') ?? '');
      if (toggled !== '') fields['fictionalBackstory.enabled'] = toggled === 'true';
      // Arabic rows are the only owner-facing editor; the technical keys are generated here and
      // stay inside the versioned document so schema validation still applies server-side.
      fields['fictionalBackstory.items'] = storyItems
        .filter((item) => item.label.trim() !== '' && item.value.trim() !== '')
        .map((item, index) => ({
          key: item.key || 'story_' + String(index + 1),
          label: item.label.trim(),
          value: item.value.trim()
        }));
      fields['knowledge.domains'] = domainRows
        .filter((domain) => domain.label.trim() !== '')
        .map((domain, index) => ({
          key: domain.key || 'domain_' + String(index + 1),
          label: domain.label.trim(),
          level: domain.level,
          state: domain.state,
          provider: null,
          lastReviewedAt: null,
          note: domain.note.trim() === '' ? null : domain.note.trim()
        }));
      const draftVersion = await api.personaDraft(persona.id, fields, 'تعديل من لوحة المالك', persona.definitionVersionId);
      const diff = await api.personaDiff(persona.id, draftVersion.versionId);
      const summary = diff.changes.map((change) => change.field + ': ' + JSON.stringify(change.from) + ' → ' + JSON.stringify(change.to)).join('\n');
      if (!confirm('معاينة الفروق قبل التفعيل:\n' + (summary || 'لا فروق') + '\n\nهل تريد تفعيل الإصدار ' + draftVersion.version + '؟')) { setSaving(false); return; }
      await api.personaActivate(persona.id, draftVersion.versionId, 'تفعيل بعد معاينة الفروق', persona.definitionVersionId);
      setDraft({});
      setNotice('فُعّل الإصدار ' + draftVersion.version + ' من شخصية رامي.');
      await reload();
    } catch (caught) { alert(errorMessage(caught)); } finally { setSaving(false); }
  }

  async function ensureIdentity() {
    if (!persona) return;
    setSaving(true);
    try {
      const result = await api.ensurePersonaIdentity(persona.id);
      setNotice(result.applied ? 'استُكملت الهوية الابتدائية في إصدار ' + result.version + '.' : 'الهوية مكتملة بالفعل.');
      await reload();
    } catch (caught) { alert(errorMessage(caught)); } finally { setSaving(false); }
  }

  async function addItem(kind: 'opinion' | 'interest') {
    if (!persona) return;
    const topic = prompt(kind === 'interest' ? 'الاهتمام' : 'الموضوع');
    if (!topic) return;
    const stance = prompt(kind === 'interest' ? 'وصف الاهتمام' : 'الرأي');
    if (!stance) return;
    setSaving(true);
    try {
      await api.personaItem(persona.id, { kind, topic, stance });
      setNotice('أُضيف العنصر كمصدر معتمد من المالك.');
      await reload();
    } catch (caught) { alert(errorMessage(caught)); } finally { setSaving(false); }
  }

  const communication = (document?.['communication'] ?? {}) as Record<string, string | null>;
  const boundaries = (document?.['boundaries'] ?? {}) as { principles?: string[]; neverDoes?: string[] };
  const knowledge = (document?.['knowledge'] ?? {}) as { domains?: Array<{ label: string; level: string; state: string }>; limits?: string[] };
  const story = (document?.['fictionalBackstory'] ?? {}) as { enabled?: boolean; items?: Array<{ label: string; value: string }>; classification?: string };
  const identityCore = (document?.['identityCore'] ?? {}) as Record<string, string | null>;

  return <><PageTitle title="شخصية رامي">هوية بإصدارات، وطبقات معلنة، وسياق معاين قبل أي مزود حقيقي.</PageTitle>
    {error ? <ErrorNotice message={error} onRetry={() => void reload()} /> : null}
    {notice ? <div className="notice" role="status">{notice}</div> : null}
    {loading ? <Skeleton /> : !persona ? <Empty title="لا توجد شخصية فعالة" /> : <>
    <Panel title="الهوية الحالية" intro={`الإصدار ${persona.revision}`} actions={<Button variant="secondary" busy={saving} onClick={() => void ensureIdentity()}>استكمال الحقول المفقودة</Button>}>
      <div className="status-list">
        <div className="status-item"><span>الاسم</span><Status tone="good">{persona.name}</Status></div>
        <div className="status-item"><span>الطبيعة</span><span>{identityCore['nature'] ?? 'غير محدد'}</span></div>
        <div className="status-item"><span>اللغة واللهجة</span><span>{(identityCore['languages'] as unknown as { dialect?: string } | undefined)?.dialect ?? communication['dialect'] ?? 'غير محدد'}</span></div>
        <div className="status-item"><span>النبرة</span><span>{communication['tone'] ?? persona.tone ?? 'غير محدد'}</span></div>
        <div className="status-item"><span>التعريف</span><span>{persona.description ?? 'غير محدد'}</span></div>
        <div className="status-item"><span>بداية النشاط</span><span>{identityCore['startedAtSystem'] ?? 'غير مسجلة'}</span></div>
      </div>
      <p className="muted">حقول المالك المعلقة: {(persona.pendingOwnerDecision ?? []).join(', ') || 'لا شيء'}</p>
    </Panel>

    <Panel title="تعديل الهوية وأسلوب التواصل" intro="مسودة ثم معاينة فروق ثم تفعيل إصدار جديد.">
      <form className="form-grid" onSubmit={save}>
        {PERSONA_TEXT_FIELDS.map(([path, label]) => <label key={path}>{label}<input value={draft[path] ?? nested(document, path)} onChange={(event) => setDraft((current) => ({ ...current, [path]: event.target.value }))} /></label>)}
        <label className="full">المبادئ (سطر لكل مبدأ)<textarea rows={4} defaultValue={(boundaries.principles ?? []).join('\n')} onChange={(event) => setDraft((current) => ({ ...current, 'boundaries.principles': event.target.value.split('\n').filter(Boolean) as unknown as string }))} /></label>
        <label className="full">ما لا يفعله (سطر لكل بند)<textarea rows={4} defaultValue={(boundaries.neverDoes ?? []).join('\n')} onChange={(event) => setDraft((current) => ({ ...current, 'boundaries.neverDoes': event.target.value.split('\n').filter(Boolean) as unknown as string }))} /></label>
        <Button type="submit" busy={saving}>حفظ مسودة وتفعيل</Button>
      </form>
    </Panel>

    <Panel title="مجالات المعرفة" intro="وصف تشغيلي صادق؛ لا شهادات بشرية.">
      {knowledge.domains?.length ? <div className="card-list">{knowledge.domains.map((domain) => <article key={domain.label} className="row-between"><span>{domain.label} — {domain.level}</span><Status tone={domain.state === 'enabled' ? 'good' : 'warning'}>{domain.state}</Status></article>)}</div> : <Empty title="لا مجالات معرفة" />}
      <p className="muted">حدود: {(knowledge.limits ?? []).join(' | ') || 'غير محددة'}</p>
      <div className="card-list">{domainRows.map((domain, index) => <article key={'domain-row-' + String(index)}>
        <div className="form-grid">
          <label>اسم المجال<input value={domain.label} onChange={(event) => setDomainRows((rows) => rows.map((row, position) => position === index ? { ...row, label: event.target.value } : row))} /></label>
          <label>المستوى<select value={domain.level} onChange={(event) => setDomainRows((rows) => rows.map((row, position) => position === index ? { ...row, level: event.target.value } : row))}><option value="basic">أساسي</option><option value="operational">تشغيلي</option><option value="advanced">متقدم</option></select></label>
          <label>الحالة<select value={domain.state} onChange={(event) => setDomainRows((rows) => rows.map((row, position) => position === index ? { ...row, state: event.target.value } : row))}><option value="enabled">مفعّل</option><option value="awaiting_provider">ينتظر مزودًا</option><option value="not_configured">غير مهيأ</option></select></label>
          <label className="full">ملاحظة<input value={domain.note} onChange={(event) => setDomainRows((rows) => rows.map((row, position) => position === index ? { ...row, note: event.target.value } : row))} /></label>
        </div>
        <Button variant="danger" type="button" onClick={() => setDomainRows((rows) => rows.filter((_row, position) => position !== index))}>حذف المجال</Button>
      </article>)}</div>
      <div className="inline-actions"><Button variant="secondary" type="button" onClick={() => setDomainRows((rows) => [...rows, { key: 'domain_' + String(rows.length + 1), label: '', level: 'operational', state: 'awaiting_provider', note: '' }])}>إضافة مجال</Button></div>
    </Panel>

    <Panel title="الخلفية القصصية" intro="اختيارية ومصنّفة user_authored_fictional ولا تُقدَّم كواقع.">
      <label className="check-row"><input type="checkbox" checked={storyEnabled} onChange={(event) => setStoryEnabled(event.target.checked)} /><span>تفعيل الخلفية القصصية في السياق</span></label>
      <p className="muted">التصنيف الحالي: {story.classification ?? 'user_authored_fictional'} — العناصر: {story.items?.length ?? 0}</p>
      {story.items?.length ? <div className="card-list">{story.items.map((item) => <article key={item.label}><strong>{item.label}</strong><p>{item.value}</p></article>)}</div> : <Empty title="لا خلفية قصصية بعد" />}
      <div className="card-list">{storyItems.map((item, index) => <article key={'story-row-' + String(index)}>
        <div className="form-grid">
          <label>العنوان<input value={item.label} onChange={(event) => setStoryItems((rows) => rows.map((row, position) => position === index ? { ...row, label: event.target.value } : row))} /></label>
          <label className="full">التفصيل<input value={item.value} onChange={(event) => setStoryItems((rows) => rows.map((row, position) => position === index ? { ...row, value: event.target.value } : row))} /></label>
        </div>
        <Button variant="danger" type="button" onClick={() => setStoryItems((rows) => rows.filter((_row, position) => position !== index))}>حذف العنصر</Button>
      </article>)}</div>
      <div className="inline-actions"><Button variant="secondary" type="button" onClick={() => setStoryItems((rows) => [...rows, { key: 'story_' + String(rows.length + 1), label: '', value: '' }])}>إضافة عنصر</Button></div>
      <p className="privacy-note">تعطيل الخلفية لا يحذف الهوية ولا التجارب الحقيقية.</p>
    </Panel>

    <Panel title="السمات المكتسبة" intro="قيم حسابية مشتقة من أحداث حقيقية.">{persona.traits?.length ? <div className="card-list">{persona.traits.map((trait) => <article key={trait.id}><div className="row-between"><strong>{trait.name}</strong><span>{Math.round(trait.value * 100)}٪</span></div><Meter label={trait.reason || trait.name} value={trait.value} max={1} /></article>)}</div> : <Empty title="لا توجد سمات مكتسبة بعد" />}</Panel>

    <Panel title="الآراء والاهتمامات" intro="كل عنصر له مصدر وتاريخ وحالة." actions={<div className="inline-actions"><Button variant="secondary" busy={saving} onClick={() => void addItem('opinion')}>إضافة رأي</Button><Button variant="secondary" busy={saving} onClick={() => void addItem('interest')}>إضافة اهتمام</Button></div>}>
      {data?.profile?.items.items.length ? <div className="card-list">{data.profile.items.items.map((item) => <article key={item.id}><div className="row-between"><strong>{item.kind === 'interest' ? 'اهتمام' : 'رأي'}: {item.topic}</strong><Status tone={item.status === 'approved' ? 'good' : item.status === 'proposed' ? 'warning' : 'bad'}>{item.status}</Status></div><p>{item.stance}</p><p className="muted">المصدر: {item.origin === 'owner' ? 'إدخال المالك' : 'تطور مكتسب'} — الثقة {Math.round(item.confidence * 100)}٪ — {formatDate(item.updatedAt)}</p><div className="inline-actions"><Button variant="secondary" busy={saving} onClick={() => void api.personaItemDecision(persona.id, item.id, 'invalidate').then(() => reload()).catch((caught) => alert(errorMessage(caught)))}>إبطال</Button></div></article>)}</div> : <Empty title="لا آراء أو اهتمامات بعد" />}
    </Panel>

    <Panel title="تاريخ النمو والتجارب">{data?.profile?.timeline.length ? <div className="card-list">{data.profile.timeline.slice(0, 20).map((entry) => <article key={entry.id}><div className="row-between"><strong>{entry.summary}</strong><span className="muted">{entry.kind}</span></div><p className="muted">{formatDate(entry.occurredAt)} — تصنيف: {entry.truthClass}{entry.sourceType ? ` — مصدر: ${entry.sourceType}` : ''}</p></article>)}</div> : <Empty title="لا أحداث بعد" />}</Panel>

    <PersonaControlPanels personaId={persona.id} document={document} versionId={persona.definitionVersionId} onChanged={() => void reload()} />
    <details className="technical-details"><summary>تفاصيل تقنية (للمراجعة المتقدمة)</summary>
      <p className="muted">هذه معرّفات ومفاتيح داخلية تُدار خادميًا؛ لا تحتاج تعديلها للاستخدام اليومي.</p>
      <pre>{JSON.stringify({ identityCore, communication, profile: document?.['profile'] ?? null, boundaries, knowledge, fictionalBackstory: story, contextPolicy: document?.['contextPolicy'] ?? null, personaVersion: persona?.revision ?? null }, null, 2)}</pre>
    </details>
    <Panel title="إصدارات الشخصية" intro="تفعيل إصدار أو تراجع محدد دون المساس بالذكريات.">{data?.profile?.versions.length ? <div className="card-list">{data.profile.versions.map((version) => <article key={version.versionId} className="row-between"><span>إصدار {version.version} — {version.shared ? 'مشترك' : 'خاص بالمالك'} — {version.state}</span>{version.active ? <Status tone="good">نشط</Status> : <Button variant="secondary" busy={saving} onClick={() => { if (confirm('تفعيل الإصدار ' + String(version.version) + '؟ لن تتغير الذكريات أو المحادثات.')) { void api.personaActivate(persona.id, version.versionId, 'تفعيل من قائمة الإصدارات', persona.definitionVersionId).then(() => reload()).catch((caught) => alert(errorMessage(caught))); } }}>تفعيل</Button>}</article>)}</div> : <Empty title="لا إصدارات" />}</Panel>

    <Panel title="معاينة السياق" intro="ما سيصل إلى النموذج فعليًا، ولماذا، وما استُثني.">
      {data?.context ? <>
        <p className="muted">الميزانية {data.context.budget} حرفًا — المستخدم {data.context.usedCharacters} — {data.context.truncated ? 'مقطوع داخل الميزانية' : 'داخل الميزانية'} — عناصر مستثناة: {data.context.excludedMemoryCount}</p>
        {data.context.timings ? <p className="muted">زمن تحضير السياق محليًا: {data.context.timings.contextPrepareMs} م.ث — قياسات المزود والنقل والصوت غير متاحة قبل المرحلة 11.9.</p> : null}
        <div className="card-list">{data.context.sections.map((section) => <article key={section.id}><div className="row-between"><strong>{section.title}</strong><Status tone={section.status === 'included' ? 'good' : 'warning'}>{section.status === 'included' ? 'مضمّن' : 'مستثنى: ' + (section.excludedReason ?? 'ميزانية')}</Status></div><p className="muted">السبب: {section.reason} — عناصر {section.itemCount} — أحرف {section.characters} — الأولوية {section.priority}</p></article>)}</div>
        {data.context.excluded.length ? <p className="muted">استُثني: {data.context.excluded.map((entry) => entry.id + ' (' + entry.reason + ')').join(' | ')}</p> : null}
      </> : <Empty title="لا معاينة متاحة" />}
    </Panel>
    <Panel title="تراجع محدد عن سمة" intro="يعيد تعريف الشخصية فقط، ولا يعيد الذكريات أو المحادثات."><form className="inline-form" onSubmit={(event) => { event.preventDefault(); void (async () => { const traitKey = prompt('اسم السمة (مثال curiosity)'); if (!traitKey) return; const raw = prompt('القيمة المستهدفة بين 0 و 1'); if (raw === null) return; const target = Number(raw); if (!Number.isFinite(target) || target < 0 || target > 1) { alert('قيمة غير صالحة'); return; } const result = await api.personaRollbackTrait(persona.id, traitKey, target, 'تراجع محدد من لوحة المالك'); alert(result.applied ? 'تم التراجع المحدد عن السمة.' : 'لم يُطبَّق: ' + result.code); await reload(); })().catch((caught) => alert(errorMessage(caught))); }}><label>اسم السمة<input name="revision" type="number" min="1" max={persona.revision - 1} required /></label><Button variant="secondary" type="submit">معاينة وطلب التراجع</Button></form></Panel>
  </>}</>;
}

export function AboutRamiPage() {
  const loader = useCallback(() => api.personas().then(async (page) => {
    const persona = page.items[0];
    if (!persona) return { persona: null, profile: null };
    return { persona, profile: await api.personaProfile(persona.id) };
  }), []);
  const { data, error, loading, reload } = useLoad(loader);
  const persona = data?.persona ?? null;
  const document = (data?.profile?.persona ?? null) as unknown as Record<string, unknown> | null;
  const identityCore = (document?.['identityCore'] ?? {}) as Record<string, unknown>;
  const communication = (document?.['communication'] ?? {}) as Record<string, string | null>;
  const profile = (document?.['profile'] ?? {}) as Record<string, string | null>;
  const boundaries = (document?.['boundaries'] ?? {}) as { principles?: string[]; neverDoes?: string[] };
  const knowledge = (document?.['knowledge'] ?? {}) as { domains?: Array<{ label: string; level: string; state: string }>; limits?: string[] };
  const story = (document?.['fictionalBackstory'] ?? {}) as { enabled?: boolean; classification?: string; items?: Array<{ label: string; value: string }> };
  return <><PageTitle title="عن رامي">هذه صفحة عرض مقروءة، وليست محررًا إداريًا.</PageTitle>
    {error ? <ErrorNotice message={error} onRetry={() => void reload()} /> : null}{loading ? <Skeleton /> : !persona ? <Empty title="لا شخصية فعالة" /> : <>
    <Panel title="من هو رامي"><p>{profile['short'] ?? persona.description ?? ''}</p><p className="muted">{profile['extended'] ?? ''}</p><p className="muted">الدور: {profile['role'] ?? '—'} — النشاط داخل النظام منذ {String(identityCore['startedAtSystem'] ?? '—')}</p></Panel>
    <Panel title="طريقته في الكلام"><p>{communication['tone'] ?? '—'}</p><p className="muted">اللهجة: {communication['dialect'] ?? '—'} — المباشرة: {communication['directness'] ?? '—'} — الصراحة: {communication['honestyStyle'] ?? '—'}</p></Panel>
    <Panel title="قيمه وحدوده"><div className="card-list">{(boundaries.principles ?? []).map((item) => <article key={item}>{item}</article>)}</div><p className="muted">{(boundaries.neverDoes ?? []).join(' | ')}</p></Panel>
    <Panel title="مجالات معرفته"><div className="card-list">{(knowledge.domains ?? []).map((domain) => <article key={domain.label} className="row-between"><span>{domain.label} — {domain.level}</span><Status tone={domain.state === 'enabled' ? 'good' : 'warning'}>{domain.state}</Status></article>)}</div><p className="muted">{(knowledge.limits ?? []).join(' | ')}</p></Panel>
    {story.enabled && story.items?.length ? <Panel title="خلفية قصصية" intro="من تأليف المالك — ليست أحداثًا حقيقية"><div className="card-list">{story.items.map((item) => <article key={item.label}><strong>{item.label}</strong><p>{item.value}</p></article>)}</div></Panel> : null}
    <Panel title="محطات من تاريخه">{data?.profile?.timeline.length ? <div className="card-list">{data.profile.timeline.slice(0, 10).map((entry) => <article key={entry.id}><strong>{entry.summary}</strong><p className="muted">{formatDate(entry.occurredAt)}</p></article>)}</div> : <Empty title="لا محطات بعد" />}</Panel>
  </>}</>;
}

export function PrivacyPage() {
  const loader = useCallback(() => api.deletionRequests(), []);
  const { data, error, loading, reload } = useLoad(loader);
  return <><PageTitle title="الخصوصية والحذف">الحذف يبدأ بحاجز فوري؛ وانتهاء النسخ المشفرة يتبع مدة الاحتفاظ.</PageTitle>
    {error ? <ErrorNotice message={error} onRetry={() => void reload()} /> : null}
    <Panel title="طلبات الحذف"><p className="muted">الحجب الفوري محلي؛ منع الاستعادة يظهر مثبتًا بعد تأكيد خارجي فقط.</p>{loading ? <Skeleton /> : data?.items.length ? <div className="card-list">{data.items.map((item) => <article key={item.id}><div className="row-between"><strong>{item.scope} — {item.targetIds.length} عنصرًا</strong><Status tone={item.status === 'complete' || item.status === 'restored' ? 'good' : 'warning'}>{statusLabel(item.status)}</Status></div><details className="technical-details"><summary>تفاصيل تقنية</summary><p className="muted">المعرّف التقني للطلب: {item.id}</p></details><p className="muted">بدأ {formatDate(item.requestedAt)}{item.completedAt ? ` — اكتمل ${formatDate(item.completedAt)}` : ''}</p><p className="muted">منع الاستعادة: {item.externalPreventionState}{item.progress && typeof item.progress === 'object' && 'percent' in item.progress ? ` — التقدم ${String((item.progress as { percent?: number }).percent)}%` : ''}</p>{item.steps?.length ? <p className="muted">{describeSteps(item)}</p> : null}<p className="muted">النوع: {item.scope === 'memory' || item.scope === 'fact' ? 'ذكرى' : item.scope === 'message' ? 'رسالة' : item.scope === 'conversation' ? 'محادثة' : 'تسجيل'} — النمط: {item.mode === 'trash' ? 'سلة محذوفات' : 'حذف نهائي'} — الحالة: {statusLabel(item.status)}</p></article>)}</div> : <Empty title="لا طلبات حذف" />}</Panel>
    <Panel title="الفرق الذي يجب أن يكون واضحًا"><p>الحذف من الاستخدام والبحث والسياق يحدث فورًا عند التنفيذ.</p><p className="muted">أما بايتات النسخ الاحتياطية المشفرة فتنتهي وفق سياسة الاحتفاظ، وقد توجد نسخة خارجية حتى انتهائها؛ لا يُدّعى محو لحظي لدى كل جهة.</p></Panel>
  </>;
}

export function MemoryPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState({ search: '', status: '' });
  const loader = useCallback(async () => {
    const [candidates, records] = await Promise.all([
      api.memories(query.search, query.status),
      api.memoryItems(query.search).catch(() => ({ items: [] as MemoryRecord[], nextCursor: null }))
    ]);
    return { candidates, records };
  }, [query]);
  const { data, error, loading, reload } = useLoad(loader);
  const [busyId, setBusyId] = useState('');
  const [hits, setHits] = useState<{ items: MemoryItem[]; mode: string; degraded: boolean } | null>(null);
  const [notice, setNotice] = useState('');
  const memoryId = (item: MemoryItem) => item.memoryId ?? item.id;
  const truthLabel = (value: string): string => TRUTH_LABELS[value] ?? value;
  async function trashRecord(record: MemoryRecord) {
    setBusyId(record.id); setNotice('');
    try {
      await api.moveToTrash('memory', [record.id]);
      setNotice('نُقلت الذكرى إلى سلة المحذوفات، ويمكن استرجاعها من صفحة السلة.');
      await reload();
    } catch (caught) { alert(errorMessage(caught)); } finally { setBusyId(''); }
  }
  async function forgetRecord(record: MemoryRecord) {
    setBusyId(record.id); setNotice('');
    try {
      const preview = await api.previewPermanentDeletion('memory', [record.id]);
      if (!confirm(describePermanent(preview))) { setBusyId(''); return; }
      await api.confirmDeletion(preview.id);
      setNotice('حُذفت الذكرى نهائيًا مع مشتقاتها.');
      await reload();
    } catch (caught) { alert(errorMessage(caught)); } finally { setBusyId(''); }
  }
  async function decide(item: MemoryItem, decision: 'approve' | 'reject') {
    try { await api.decideMemory(item.id, decision, item.revision); await reload(); } catch (caught) { alert(errorMessage(caught)); }
  }
  async function runSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setNotice('');
    try {
      const result = await api.searchMemories(search || query.search, 10);
      setHits({ items: result.items.map((hit) => ({ id: hit.memoryId, memoryId: hit.memoryId, text: hit.text, status: 'approved', isSearchHit: true, ...(hit.statementKind ? { kind: hit.statementKind } : {}), source: { id: hit.sourceId } })), mode: result.mode, degraded: result.degraded });
      if (!result.items.length) setNotice('لم يُعثر على ذكرى مطابقة ضمن البيانات المعتمدة.');
    } catch (caught) { alert(errorMessage(caught)); }
  }
  async function correct(item: MemoryItem) {
    const text = prompt('النص المصحّح للذاكرة:', item.text);
    if (!text || text === item.text) return;
    setBusyId(item.id);
    try {
      await api.correctMemory(memoryId(item), text, item.memoryVersion ?? 1, 'تصحيح من صفحة الذاكرة');
      await reload();
    } catch (caught) { alert(errorMessage(caught)); } finally { setBusyId(''); }
  }
  async function moveToTrash(item: MemoryItem) {
    setBusyId(item.id);
    try {
      await api.moveToTrash('memory', [memoryId(item)]);
      setNotice('نُقلت الذكرى إلى سلة المحذوفات، ويمكن استرجاعها من صفحة السلة.');
      await reload();
    } catch (caught) { alert(errorMessage(caught)); } finally { setBusyId(''); }
  }
  async function forget(item: MemoryItem) {
    setBusyId(item.id);
    try {
      const preview = await api.requestDeletion('memory', [memoryId(item)]);
      const sources = (preview.preview as { memories?: Array<{ sourceCount?: number; embeddingCount?: number }> }).memories ?? [];
      const summary = sources.length ? `المصادر المتأثرة: ${sources[0]?.sourceCount ?? 0}، المتجهات: ${sources[0]?.embeddingCount ?? 0}` : 'لا توجد تفاصيل إضافية.';
      if (!confirm(`${summary}\nسيُحجب العنصر ومشتقاته فورًا، ويُنشأ Tombstone يمنع إعادة الاستخراج من مصدره. هل تريد التنفيذ؟`)) return;
      await api.confirmDeletion(preview.id);
      await reload();
    } catch (caught) { alert(errorMessage(caught)); } finally { setBusyId(''); }
  }
  const results = hits?.items ?? data?.candidates.items ?? [];
  const records = data?.records.items ?? [];
  return <><PageTitle title="الذاكرة">ابحث في المصادر، وراجع المرشحات، وصحح أو انسَ بنطاق صريح.</PageTitle>
    <Panel title="البحث والفلاتر"><form className="inline-form" onSubmit={(event) => { event.preventDefault(); setQuery({ search, status }); }}><label>بحث<input type="search" value={search} onChange={(event) => setSearch(event.target.value)} /></label><label>الحالة<select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">الكل</option><option value="approved">معتمدة</option><option value="needs_clarification">تحتاج توضيحًا</option><option value="pending_validation">قيد المراجعة</option><option value="rejected">مرفوضة</option></select></label><Button type="submit">تصفية</Button><Button variant="secondary" type="button" onClick={(event) => void runSearch(event as unknown as FormEvent<HTMLFormElement>)}>بحث دلالي/نصي</Button></form>
      {hits ? <div className="notice" role="status">{hits.degraded ? 'الوضع: نصي فقط — المتجهات غير مفعّلة (awaiting_credentials).' : 'الوضع: بحث هجين.'}</div> : null}</Panel>
    {notice ? <div className="notice" role="status">{notice}</div> : null}
    {error ? <ErrorNotice message={error} onRetry={() => void reload()} /> : null}{loading ? <Skeleton lines={5} /> : results.length ? <div className="card-list">{results.map((item) => <article key={item.id} className="memory-card"><div className="row-between"><Status tone={item.status === 'approved' ? 'good' : item.status === 'rejected' ? 'bad' : 'warning'}>{statusLabel(item.status)}</Status><span>{formatDate(item.createdAt)}</span></div><p>{item.text}</p><p className="muted">المصدر: {item.source?.label || item.source?.id || 'غير معروض'}{item.kind ? ` — نوع القول: ${item.kind}` : ''}</p><div className="inline-actions">{!item.isSearchHit && item.status !== 'approved' ? <Button variant="secondary" busy={busyId === item.id} onClick={() => void decide(item, 'approve')}>اعتماد</Button> : null}{!item.isSearchHit ? <Button variant="secondary" onClick={() => void decide(item, 'reject')}>رفض الحفظ</Button> : null}{!item.isSearchHit && item.memoryVersion ? <Button variant="secondary" busy={busyId === item.id} onClick={() => void correct(item)}>تصحيح</Button> : null}{!item.isSearchHit ? <Button variant="secondary" busy={busyId === item.id} onClick={() => void moveToTrash(item)}>نقل إلى السلة</Button> : null}<Button variant="danger" busy={busyId === item.id} onClick={() => void forget(item)}>حذف نهائي (نسيان)</Button></div></article>)}</div> : <Empty title="لا توجد نتائج مطابقة" />}
    <Panel title="الذكريات المعتمدة" intro="ما يعرفه رامي عنك فعلًا، بمصدره وتصنيفه؛ النقل إلى السلة قابل للاسترجاع والحذف النهائي غير قابل للتراجع.">
      {loading ? <Skeleton /> : records.length ? <div className="card-list">{records.map((record) => <article key={record.id}>
        <div className="row-between"><Status tone={record.truthClass === 'uncertain' ? 'warning' : record.truthClass === 'invalidated' ? 'bad' : 'good'}>{truthLabel(record.truthClass)}</Status><span className="muted">{formatDate(record.updatedAt)}</span></div>
        <p>{record.text}</p>
        <p className="muted">المصادر: {record.sourceCount} — المشتقات: {record.derivations.embeddings} متجهًا — النوع: {record.memoryType}</p>
        <div className="inline-actions">
          <Button variant="secondary" busy={busyId === record.id} onClick={() => void trashRecord(record)}>نقل إلى السلة</Button>
          <Button variant="danger" busy={busyId === record.id} onClick={() => void forgetRecord(record)}>حذف نهائي (نسيان)</Button>
        </div>
      </article>)}</div> : <Empty title="لا ذكريات معتمدة بعد" />}
    </Panel></>;
}

export function ConversationsPage() {
  const loader = useCallback(async () => ({ conversations: (await api.conversations()).items, recordings: (await api.recordings()).items }), []);
  const { data, error, loading, reload } = useLoad(loader);
  const [openId, setOpenId] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  async function open(conversationId: string) {
    setNotice('');
    try {
      const page = await api.messages(conversationId);
      setMessages([...page.items].reverse());
      setOpenId(conversationId);
    } catch (caught) { alert(errorMessage(caught)); }
  }
  async function trashMessage(id: string) {
    setBusy(true); setNotice('');
    try {
      await api.moveMessageToTrash(id);
      setMessages((current) => current.filter((item) => item.id !== id));
      setNotice('نُقلت الرسالة إلى سلة المحذوفات، ويمكن استرجاعها من صفحة السلة.');
    } catch (caught) { alert(errorMessage(caught)); } finally { setBusy(false); }
  }
  async function trashConversation(id: string) {
    if (!confirm('نقل المحادثة إلى سلة المحذوفات؟ يمكن استرجاعها لاحقًا من صفحة السلة.')) return;
    setBusy(true); setNotice('');
    try {
      await api.moveConversationToTrash(id);
      setOpenId(''); setMessages([]);
      setNotice('نُقلت المحادثة إلى سلة المحذوفات، ويمكن استرجاعها من صفحة السلة.');
      await reload();
    } catch (caught) { alert(errorMessage(caught)); } finally { setBusy(false); }
  }
  async function removeMessage(id: string) {
    setBusy(true); setNotice('');
    try {
      const preview = await api.previewPermanentDeletion('message', [id]);
      if (!confirm(describePermanent(preview))) { setBusy(false); return; }
      const request = await api.confirmDeletion(preview.id);
      setMessages((current) => current.filter((item) => item.id !== id));
      setNotice('حُذفت الرسالة نهائيًا. ' + describeSteps(request));
    } catch (caught) { alert(errorMessage(caught)); } finally { setBusy(false); }
  }
  async function removeConversation(id: string) {
    setBusy(true); setNotice('');
    try {
      const preview = await api.previewPermanentDeletion('conversation', [id]);
      if (!confirm(describePermanent(preview))) { setBusy(false); return; }
      const request = await api.confirmDeletion(preview.id);
      setOpenId(''); setMessages([]);
      setNotice('حُذفت المحادثة نهائيًا. ' + describeSteps(request));
      await reload();
    } catch (caught) { alert(errorMessage(caught)); } finally { setBusy(false); }
  }
  return <><PageTitle title="المحادثات والتسجيلات">السجل النصي والتسجيلات منفصلان؛ لكل نطاق حذف أثره المعلن.</PageTitle>
    {error ? <ErrorNotice message={error} onRetry={() => void reload()} /> : null}
    {notice ? <div className="notice" role="status">{notice}</div> : null}
    {loading ? <Skeleton /> : <div className="dashboard-grid">
      <Panel title="المحادثات">{data?.conversations.length ? <div className="card-list">{data.conversations.map((item) => <article key={item.id}><div className="row-between"><strong>{item.title || 'محادثة رامي'}</strong><span className="muted">{formatDate(item.updatedAt)}</span></div><div className="inline-actions"><Button variant="secondary" busy={busy} onClick={() => void open(item.id)}>عرض الرسائل</Button><Button variant="secondary" busy={busy} onClick={() => void trashConversation(item.id)}>نقل المحادثة إلى السلة</Button><Button variant="danger" busy={busy} onClick={() => void removeConversation(item.id)}>حذف المحادثة نهائيًا</Button></div></article>)}</div> : <Empty title="لا توجد محادثات" />}</Panel>
      <Panel title={openId ? 'رسائل المحادثة' : 'الرسائل'} intro="الحذف هنا يحذف النص ومشتقاته الحصرية فقط.">{openId ? (messages.length ? <div className="card-list">{messages.map((message) => <article key={message.id}><div className="row-between"><strong>{message.role === 'owner' ? 'فارس' : message.role === 'rami' ? 'رامي' : 'النظام'}</strong><span className="muted">{formatDate(message.createdAt)}</span></div><p>{message.text}</p><div className="inline-actions"><Button variant="secondary" busy={busy} onClick={() => void trashMessage(message.id)}>نقل الرسالة إلى السلة</Button><Button variant="danger" busy={busy} onClick={() => void removeMessage(message.id)}>حذف الرسالة نهائيًا</Button></div></article>)}</div> : <Empty title="لا رسائل" />) : <p className="muted">اختر محادثة لعرض رسائلها.</p>}</Panel>
      <Panel title="التسجيلات">{data?.recordings.length ? <RecordingList items={data.recordings} reload={reload} /> : <Empty title="لا توجد تسجيلات" />}</Panel>
    </div>}</>;
}

function RecordingList({ items, reload }: { items: Recording[]; reload: () => Promise<void> }) {
  const [busy, setBusy] = useState('');
  async function trash(id: string) {
    if (!confirm('نقل التسجيل إلى سلة المحذوفات؟ الصوت والنص والذاكرة تبقى كما هي حتى تقرر الحذف النهائي.')) return;
    setBusy(id + ':trash');
    try { await api.moveToTrash('recording', [id]); await reload(); }
    catch (caught) { alert(errorMessage(caught)); }
    finally { setBusy(''); }
  }
  async function remove(id: string, scope: 'audio' | 'audio_derivatives' | 'all') {
    const labels: Record<string, string> = {
      audio: 'سيُحذف الصوت فقط. النص والذاكرة يبقيان.',
      audio_derivatives: 'سيُحذف الصوت والمشتقات الصوتية (النص). الذكريات تبقى.',
      all: 'سيُحذف الصوت والنص والذكريات الناتجة عنه.'
    };
    if (!confirm(labels[scope] + '\nمتابعة؟')) return;
    setBusy(id + ':' + scope);
    try { await api.deleteRecordingScoped(id, scope); await reload(); }
    catch (caught) { alert(errorMessage(caught)); }
    finally { setBusy(''); }
  }
  return <div className="card-list">{items.map((item) => <article key={item.id}><div className="row-between"><Status tone={item.status === 'complete' || item.status === 'retained' ? 'good' : 'warning'}>{statusLabel(item.status)}</Status><span>{formatDuration(item.durationMs)}</span></div><p>{formatBytes(item.size)} {item.missing?.length ? `— ${item.missing.length} قطعة مفقودة` : '— لا فجوات معلنة'}</p><div className="inline-actions"><a className="button button--secondary" href={`/api/v1/recordings/${encodeURIComponent(item.id)}/export`} download>تنزيل MP3</a>{!item.retained && item.status !== 'deleted' ? <Button variant="secondary" onClick={() => void api.retainRecording(item.id).then(() => reload()).catch((caught) => alert(errorMessage(caught)))}>احتفاظ داخل رامي</Button> : null}</div>{item.status !== 'deleted' ? <div className="inline-actions"><Button variant="secondary" busy={busy === item.id + ':trash'} onClick={() => void trash(item.id)}>نقل إلى السلة</Button><Button variant="danger" busy={busy === item.id + ':audio'} onClick={() => void remove(item.id, 'audio')}>حذف الصوت</Button><Button variant="danger" busy={busy === item.id + ':audio_derivatives'} onClick={() => void remove(item.id, 'audio_derivatives')}>حذف الصوت والنص</Button><Button variant="danger" busy={busy === item.id + ':all'} onClick={() => void remove(item.id, 'all')}>حذف كل ما نتج عنه</Button></div> : <Status tone="bad">الصوت محذوف؛ التفريغ والذاكرة يتبعان النطاق المختار</Status>}<p className="privacy-note">النسخة المصدرة العادية غير مشفرة.</p></article>)}</div>;
}

export function DevicesPage() {
  const loader = useCallback(async () => ({ devices: (await api.devices()).items, sessions: (await api.sessions()).items }), []);
  const { data, error, loading, reload } = useLoad(loader);
  const [pushBusy, setPushBusy] = useState(false);
  async function togglePush(enabled: boolean) {
    setPushBusy(true);
    try {
      const registration = await registerPwa(() => undefined);
      if (!registration) throw new Error('هذا المتصفح لا يدعم تطبيق الويب.');
      if (enabled) await enablePush(registration); else await disablePush(registration);
      await reload();
    } catch (caught) { alert(errorMessage(caught)); } finally { setPushBusy(false); }
  }
  return <><PageTitle title="الأجهزة والتواصل">الإشعار دعوة فقط؛ لا يفتح ميكروفونًا أو اتصالًا مدفوعًا تلقائيًا.</PageTitle>{error ? <ErrorNotice message={error} onRetry={() => void reload()} /> : null}{loading ? <Skeleton /> : <div className="dashboard-grid"><Panel title="الأجهزة">{data?.devices.length ? <DeviceList items={data.devices} reload={reload} /> : <Empty title="لا توجد أجهزة مسجلة" />}</Panel><Panel title="الإشعارات والمبادرات"><p>اطلب صلاحية الإشعارات عندما ترغب بتلقي دعوات عامة وآمنة.</p><div className="inline-actions"><Button busy={pushBusy} onClick={() => void togglePush(true)}>تفعيل الإشعارات</Button><Button variant="secondary" busy={pushBusy} onClick={() => void togglePush(false)}>إيقافها على هذا الجهاز</Button></div></Panel><Panel title="الجلسات">{data?.sessions.map((session) => <article key={session.id} className="row-between"><span>{session.deviceName || 'جهاز غير مسمى'} {session.current ? '(الحالي)' : ''}</span>{!session.current ? <Button variant="danger" onClick={() => void api.revokeSession(session.id).then(() => reload())}>إلغاء</Button> : null}</article>)}</Panel></div>}</>;
}

function DeviceList({ items, reload }: { items: Device[]; reload: () => Promise<void> }) {
  return <div className="card-list">{items.map((device) => <article key={device.id}><div className="row-between"><strong>{device.name}</strong>{device.current ? <Status tone="good">هذا الجهاز</Status> : null}</div><p className="muted">آخر ظهور: {formatDate(device.lastSeenAt)}</p><div className="inline-actions">{!device.preferred ? <Button variant="secondary" onClick={() => void api.setPreferredDevice(device.id).then(() => reload())}>جعله مفضلًا</Button> : <Status tone="good">مفضل</Status>} {!device.current ? <Button variant="danger" onClick={() => { if (confirm('إلغاء الجهاز ينهي جلساته وبثه وصوته.')) void api.revokeDevice(device.id).then(() => reload()); }}>إلغاء الجهاز</Button> : null}</div></article>)}</div>;
}

export function ProvidersPage() {
  return <><PageTitle title="الذكاء والمزودات">مسودة، تحقق بنيوي مجاني، اختبار محدود، ثم تفعيل إصدار داخلي. الاتصال الخارجي بالمزود مقفل حتى المرحلة 11.9.</PageTitle><ProviderActivationPanels /></>;
}

export function UsagePage() {
  const loader = useCallback(async () => {
    const [usage, budget, operations] = await Promise.all([api.usage(), api.budget(), api.operations()]);
    return { usage, budget, operations: operations.items };
  }, []);
  const { data, error, loading, reload } = useLoad(loader);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const scope = String(form.get('scope')) === 'development' ? 'development' : 'operations';
    const read = (value: FormDataEntryValue | null): number | null => {
      const text = String(value ?? '').trim();
      if (text === '') return null;
      const parsed = Number(text);
      return Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : null;
    };
    setSaving(true); setNotice('');
    try {
      const saved = await api.saveBudget({ scope, dailyLimitMicrounits: read(form.get('daily')), monthlyLimitMicrounits: read(form.get('monthly')) });
      setNotice('حُفظت مسودة الميزانية للإصدار ' + saved.revision + ' وتحتاج تفعيلًا لتصبح سارية.');
      await reload();
    } catch (caught) { setNotice(errorMessage(caught)); } finally { setSaving(false); }
  }

  async function activate(policy: BudgetPolicy) {
    setNotice('');
    try {
      const activated = await api.activateBudget(policy.scope, policy.revision);
      setNotice('فُعّلت ' + scopeLabel(policy.scope) + ' للإصدار ' + activated.revision + '.');
      await reload();
    } catch (caught) { setNotice(errorMessage(caught)); }
  }

  const policies = data?.budget.items ?? [];
  return <><PageTitle title='الاستخدام والموارد'>ميزانية تطوير وميزانية تشغيل مستقلتان؛ اليوم بتوقيت الرياض والتخزين UTC.</PageTitle>
    {error ? <ErrorNotice message={error} onRetry={() => void reload()} /> : null}
    {loading ? <Skeleton /> : data ? <div className='dashboard-grid'>
      <Panel title='الملخص'><UsageCards usage={data.usage} /></Panel>
      <Panel title='حدود الميزانية' intro='غياب السقف يُبقي العمل المدفوع معطلًا ولا يمنع المحاكيات المجانية.'>
        {policies.length ? <div className='card-list'>{policies.map((policy) => <article key={policy.scope}>
          <div className='row-between'><strong>{scopeLabel(policy.scope)}</strong><Status tone={policy.state === 'active' ? 'good' : 'warning'}>{policy.state === 'active' ? 'سارية' : 'مسودة'}</Status></div>
          <p className='muted'>يومي: {policy.dailyLimitMicrounits ?? 'غير محدد'} — شهري: {policy.monthlyLimitMicrounits ?? 'غير محدد'} — الإصدار {policy.revision}</p>
          {policy.state === 'active' ? null : <Button variant='secondary' onClick={() => void activate(policy)}>تفعيل الحدود</Button>}
        </article>)}</div> : <Empty title='لا توجد ميزانية محفوظة' />}
        <form className='form-grid' onSubmit={save}>
          <label>النطاق<select name='scope' defaultValue='operations'><option value='operations'>التشغيل</option><option value='development'>التطوير</option></select></label>
          <label>الحد اليومي<input name='daily' type='number' min='0' step='1' inputMode='numeric' /></label>
          <label>الحد الشهري<input name='monthly' type='number' min='0' step='1' inputMode='numeric' /></label>
          <Button type='submit' busy={saving}>حفظ مسودة الميزانية</Button>
        </form>
        {notice ? <p className='muted' role='status'>{notice}</p> : null}
      </Panel>
      <Panel title='التنبيهات'>{data.usage.alerts?.length ? <div className='status-list'>{data.usage.alerts.map((alert) => <div className='status-item' key={alert.scope + alert.periodKind + alert.threshold}><span>{scopeLabel(alert.scope === 'development' ? 'development' : 'operations')} — {alert.periodKind === 'daily' ? 'يومي' : 'شهري'}</span><Status tone={alert.threshold >= 90 ? 'bad' : 'warning'}>{alert.threshold + '٪'}</Status></div>)}</div> : <Empty title='لا توجد تنبيهات مسجلة' />}</Panel>
      <Panel title='الطوابير والعمليات'><OperationList operations={data.operations} /></Panel>
    </div> : null}
  </>;
}

function scopeLabel(scope: 'development' | 'operations'): string {
  return scope === 'development' ? 'ميزانية التطوير' : 'ميزانية التشغيل';
}

function budgetLevelLabel(level: string): string {
  if (level === 'ok') return 'داخل الحد';
  if (level === 'warning') return 'تجاوز ٧٠٪';
  if (level === 'critical') return 'تجاوز ٩٠٪';
  if (level === 'exhausted') return 'استُنفد';
  return 'غير مهيأ';
}

function UsageCards({ usage }: { usage: UsageSummary }) {
  return <>
    <Panel title='التكلفة'><Meter label='التكلفة المسواة' value={usage.settled ?? 0} max={usage.budget && usage.budget > 0 ? usage.budget : (usage.settled ?? 0) + (usage.reserved ?? 0) + 1} /><dl className='stats'><div><dt>مقدرة</dt><dd>{usage.estimated ?? 0}</dd></div><div><dt>محجوزة</dt><dd>{usage.reserved ?? 0}</dd></div><div><dt>مسواة</dt><dd>{usage.settled ?? 0}</dd></div><div><dt>السقف</dt><dd>{usage.budget ?? 0}</dd></div></dl></Panel>
    {usage.scopes?.length ? <Panel title='النطاقات'>{usage.scopes.map((scope) => <article key={scope.scope}><div className='row-between'><strong>{scopeLabel(scope.scope)}</strong><Status tone={scope.level === 'ok' ? 'good' : scope.level === 'exhausted' ? 'bad' : 'warning'}>{budgetLevelLabel(scope.level)}</Status></div><p className='muted'>اليوم: {scope.dailyUsedMicrounits} / {scope.dailyLimitMicrounits ?? '∞'} — الشهر: {scope.monthlyUsedMicrounits} / {scope.monthlyLimitMicrounits ?? '∞'}</p></article>)}</Panel> : null}
    <Panel title='التخزين'>{usage.storage ? <><Meter label='المساحة المستخدمة' value={usage.storage.used} max={usage.storage.quota > 0 ? usage.storage.quota : usage.storage.used + 1} /><p>{formatBytes(usage.storage.used)} من {formatBytes(usage.storage.quota)}</p></> : <Empty title='لا توجد بيانات حصة' />}</Panel>
  </>;
}

export function BackupsPage() {
  const loader = useCallback(async () => {
    const [backups, bundles, sessions] = await Promise.all([api.backups(), api.recoveryBundles(), api.sessions()]);
    return { backups, bundles, sessions: sessions.items };
  }, []);
  const { data, error, loading, reload } = useLoad(loader);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  async function startBackup() {
    setBusy(true); setNotice('');
    try {
      const queued = await api.startBackup(crypto.randomUUID());
      setNotice('أُدرجت عملية نسخة اختبارية: ' + queued.operationId);
      await reload();
    } catch (caught) { setNotice(errorMessage(caught)); } finally { setBusy(false); }
  }

  async function startRestore(backup: BackupSummary) {
    if (!backup.verified) { setNotice('لا يمكن اختبار الاستعادة قبل تثبيت النسخة بمسار استعادة ناجح.'); return; }
    setBusy(true); setNotice('');
    try {
      const queued = await withReauth(() => api.testRestore(backup.id, crypto.randomUUID()));
      setNotice('أُدرجت عملية استعادة اختبارية: ' + queued.operationId);
      await reload();
    } catch (caught) { setNotice(errorMessage(caught)); } finally { setBusy(false); }
  }

  async function withReauth<T>(action: () => Promise<T>): Promise<T> {
    try {
      return await action();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'SENSITIVE_REAUTH_REQUIRED') {
        const code = prompt('أعد إدخال رمز الدخول لتأكيد العملية الحساسة');
        if (code === null || code.trim() === '') throw caught;
        await api.sensitiveReauth(code.trim());
        return await action();
      }
      throw caught;
    }
  }

  async function requestBundle(): Promise<void> {
    setBusy(true); setNotice('');
    try {
      const result = await withReauth(() => api.requestRecoveryBundle());
      setNotice('أُذن إنشاء الحزمة وسُجّل الطلب: ' + result.command);
      await reload();
    } catch (caught) { setNotice(errorMessage(caught)); } finally { setBusy(false); }
  }

  const view = data?.backups;
  return <><PageTitle title='النسخ والأمان'>نقاط استعادة مثبتة واختبارات استعادة معزولة؛ استعادة الإنتاج ليست زرًا عامًا.</PageTitle>
    {error ? <ErrorNotice message={error} onRetry={() => void reload()} /> : null}
    {loading ? <Skeleton /> : view ? <div className='dashboard-grid'>
      <Panel title='نقاط النسخ' actions={<Button variant='secondary' onClick={() => void startBackup()} busy={busy}>تشغيل نسخة اختبارية</Button>}>
        {view.items.length ? <div className='card-list'>{view.items.map((backup) => <article key={backup.id}>
          <div className='row-between'><strong>{formatDate(backup.completedAt)}</strong><Status tone={backup.verified ? 'good' : 'warning'}>{backup.verified ? 'استعادة مثبتة' : 'لم تختبر الاستعادة'}</Status></div>
          <p className='muted'>السياسة: {backup.policy ?? 'balanced'} — المفاتيح: {(backup.keyVersions ?? []).join(', ') || 'لا شيء'} — Tombstone: {backup.tombstoneVersion ?? 0}</p>
          <Button variant='secondary' onClick={() => void startRestore(backup)} busy={busy} disabled={!backup.verified}>اختبار استعادة معزولة</Button>
        </article>)}</div> : <Empty title='لا توجد نسخ مثبتة' />}
        {notice ? <p className='muted' role='status'>{notice}</p> : null}
      </Panel>
      <Panel title='اختبارات الاستعادة'>{view.restoreRuns.length ? <div className='card-list'>{view.restoreRuns.map((run) => <article key={run.id}>
        <div className='row-between'><strong>{run.mode}</strong><Status tone={run.status === 'succeeded' ? 'good' : run.status === 'failed' ? 'bad' : 'neutral'}>{run.status}</Status></div>
        <p className='muted'>معزول: {run.isolated ? 'نعم' : 'لا'} — شبكة خارجية محجوبة: {run.externalNetworkBlocked ? 'نعم' : 'لا'}</p>
        <p className='muted'>RPO: {run.measuredRpoSeconds === null ? 'غير مقاس' : run.measuredRpoSeconds + 'ث'} — RTO: {run.measuredRtoSeconds === null ? 'غير مقاس' : run.measuredRtoSeconds + 'ث'} — محاولات خروج: {run.egressAttempts} — استدعاءات مزود: {run.providerCalls}</p>
        <code dir='ltr'>{run.id}</code>
      </article>)}</div> : <Empty title='لا توجد اختبارات استعادة' />}</Panel>
      <Panel title='السياسات'>{view.policyTargets.map((target) => <article key={target.policy}><div className='row-between'><strong>{target.policy}</strong><Status tone={target.ready ? 'good' : 'warning'}>{target.ready ? 'جاهزة' : 'غير جاهزة'}</Status></div><p className='muted'>{target.note}</p></article>)}</Panel>
      <Panel title='حزمة الاسترداد' actions={<Button variant='secondary' onClick={() => void requestBundle()} busy={busy}>طلب حزمة استرداد</Button>}>{data?.bundles.items.length ? <div className='card-list'>{data.bundles.items.map((bundle) => <article key={bundle.id}><div className='row-between'><strong>{formatDate(bundle.createdAt)}</strong><Status tone={bundle.downloadedAt ? 'good' : 'neutral'}>{bundle.downloadedAt ? 'نُزّلت' : 'لم تُنزّل'}</Status></div><p className='muted'>إصدارات المفاتيح: {bundle.keyVersions.join(', ')} — VAPID: {bundle.includesVapid ? 'مضمّن' : 'غائب'} — KDF: {bundle.kdf}</p><code dir='ltr'>{bundle.sha256.slice(0, 16)}</code></article>)}</div> : <Empty title='لا توجد حزم استرداد' />}<p className='muted'>الطلب يحتاج إعادة تحقق خلال 10 دقائق؛ وبناء الملف الحساس يبقى بالأمر المحلي ولا يُعاد محتواه عبر HTTP.</p></Panel>
      <Panel title='الأمان والجلسات'><p>الجلسات النشطة: {data?.sessions.length ?? 0}</p><p className='muted'>تغيير رمز الدخول والاستعادة المحلية ينفذان من مسار آمن مع إعادة إدخال الرمز نفسه.</p></Panel>
    </div> : null}
  </>;
}

export function AdvancedPage() {
  const loader = useCallback(() => api.operations(), []);
  const { data, error, loading, reload } = useLoad(loader);
  return <><PageTitle title="السجل المتقدم">معرفات العمليات والإصدارات والأخطاء الآمنة، دون نصوص محادثة أو مفاتيح.</PageTitle><Panel title="العمليات" actions={<Button variant="secondary" onClick={() => void reload()}>تحديث</Button>}>{error ? <ErrorNotice message={error} /> : loading ? <Skeleton /> : <OperationList operations={data?.items ?? []} />}</Panel></>;
}

function OperationList({ operations }: { operations: Operation[] }) {
  if (!operations.length) return <Empty title="لا توجد عمليات" />;
  return <div className="operation-list">{operations.map((operation) => <article key={operation.id}><div className="row-between"><strong>{operation.type}</strong><Status tone={operation.status === 'succeeded' ? 'good' : operation.status === 'failed' ? 'bad' : 'neutral'}>{operation.status}</Status></div><code dir="ltr">{operation.id}</code>{typeof operation.progress === 'number' ? <Meter label="التقدم" value={operation.progress} max={100} /> : null}{operation.lastError ? <p className="error-text">{operation.lastError}</p> : null}</article>)}</div>;
}

function initiativeTone(status: string): 'good' | 'bad' | 'warning' | 'neutral' {
  if (status === 'accepted' || status === 'sent') return 'good';
  if (status === 'failed' || status === 'rejected') return 'bad';
  if (status === 'planned' || status === 'ready' || status === 'expired' || status === 'ignored') return 'warning';
  return 'neutral';
}

export function InitiativesPage() {
  const call = useCall();
  const loader = useCallback(async () => {
    const [settings, list] = await Promise.all([api.initiativeSettings(), api.initiatives()]);
    return { settings, items: list.items };
  }, []);
  const { data, error, loading, reload } = useLoad(loader);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<InitiativeSettings | null>(null);
  const [topic, setTopic] = useState('');

  useEffect(() => { if (data?.settings) setDraft(data.settings); }, [data?.settings]);

  async function saveSettings(next: Partial<InitiativeSettings>) {
    if (!draft) return;
    setBusy(true); setNotice('');
    try {
      await api.updateInitiativeSettings({ ...next, expectedRevision: draft.revision });
      await reload();
      setNotice('حُفظت إعدادات المبادرات.');
    } catch (caught) {
      setNotice(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function toggleTopic(name: string, enabled: boolean) {
    setBusy(true); setNotice('');
    try {
      await api.setInitiativeTopic({ topic: name, enabled });
      await reload();
    } catch (caught) {
      setNotice(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function requestCheck(kind: 'message' | 'call_invite') {
    setBusy(true); setNotice('');
    try {
      await api.requestInitiativeCheck({ kind, reason: 'فحص يدوي من لوحة المالك' });
      setNotice('أُرسل طلب الفحص؛ ستظهر النتيجة في صندوق المبادرات بعد معالجته.');
    } catch (caught) {
      setNotice(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function accept(initiative: Initiative) {
    if (!initiative.inviteId) return;
    setNotice('');
    await call.start({ inviteId: initiative.inviteId });
    await reload().catch(() => undefined);
  }

  async function reject(initiative: Initiative) {
    if (!initiative.inviteId) return;
    setBusy(true); setNotice('');
    try {
      await api.rejectInvite(initiative.inviteId);
      await reload();
    } catch (caught) {
      setNotice(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  const settings = data?.settings ?? null;
  return <>
    <PageTitle title="المبادرات والإشعارات">رامي قد يبدأ رسالة أو دعوة مكالمة ضمن حدود السياسة؛ القبول وحده يبدأ مسار الصوت.</PageTitle>
    {error ? <ErrorNotice message={error} onRetry={() => void reload()} /> : null}
    {notice ? <div className="notice" role="status">{notice}</div> : null}
    {loading || !settings ? <Skeleton lines={6} /> : <div className="dashboard-grid">
      <Panel title="السياسة وحدودها">
        <p className="muted">السياسة — الحد اليومي / دعوات الصوت / أقل فاصل: Conservative ٢/١/٤س، Medium ٤/٢/٢س، Flexible AI ٦/٢/١س. وكلها سقف تشغيلي دائم.</p>
        <div className="inline-actions">
          {(['conservative', 'medium', 'flexible'] as const).map((policy) => <Button
            key={policy}
            variant={draft?.policy === policy ? 'primary' : 'secondary'}
            busy={busy}
            onClick={() => void saveSettings({ policy })}
          >{policy === 'conservative' ? 'محافظ' : policy === 'medium' ? 'متوسط' : 'مرن'}</Button>)}
        </div>
        <div className="inline-actions">
          <Button variant={draft?.enabled ? 'secondary' : 'primary'} busy={busy} onClick={() => void saveSettings({ enabled: !(draft?.enabled ?? true) })}>
            {draft?.enabled ? 'تعطيل كل المبادرات' : 'تفعيل المبادرات'}
          </Button>
        </div>
        <p className="muted">ساعات الهدوء الحالية: {settings.quietStart}–{settings.quietEnd} بتوقيت {settings.timezone}.</p>
        <form className="inline-form" onSubmit={(event) => { event.preventDefault(); const form = new FormData(event.currentTarget); void saveSettings({ quietStart: String(form.get('quietStart')), quietEnd: String(form.get('quietEnd')), timezone: String(form.get('timezone')) }); }}>
          <label>بداية الهدوء<input name="quietStart" defaultValue={settings.quietStart} pattern="[0-2][0-9]:[0-5][0-9]" /></label>
          <label>نهاية الهدوء<input name="quietEnd" defaultValue={settings.quietEnd} pattern="[0-2][0-9]:[0-5][0-9]" /></label>
          <label>المنطقة الزمنية<input name="timezone" defaultValue={settings.timezone} /></label>
          <Button type="submit" busy={busy}>حفظ ساعات الهدوء</Button>
        </form>
      </Panel>
      <Panel title="الموضوعات">
        <p className="muted">عطّل أي موضوع فلا يبدأ رامي مبادرة حوله.</p>
        {settings.topics.length ? <div className="card-list">{settings.topics.map((entry) => <article key={entry.topic} className="row-between"><span>{entry.topic}</span><Button variant="secondary" busy={busy} onClick={() => void toggleTopic(entry.topic, !entry.enabled)}>{entry.enabled ? 'تعطيل' : 'تفعيل'}</Button></article>)}</div> : <Empty title="لا موضوعات معطّلة" />}
        <form className="inline-form" onSubmit={(event) => { event.preventDefault(); if (!topic.trim()) return; void toggleTopic(topic.trim(), false); setTopic(''); }}>
          <label>موضوع لتعطيله<input value={topic} onChange={(event) => setTopic(event.target.value)} placeholder="مثال: work" /></label>
          <Button type="submit" variant="secondary" busy={busy}>تعطيل الموضوع</Button>
        </form>
      </Panel>
      <Panel title="طلب فحص مبادرة" intro="يُنشئ الطلب عملية ذات معرّف ويعالجها العامل بشروط السياسة؛ لا يبدأ مكالمة بنفسه.">
        <div className="inline-actions">
          <Button busy={busy} onClick={() => void requestCheck('message')}>فحص إرسال رسالة</Button>
          <Button variant="secondary" busy={busy} onClick={() => void requestCheck('call_invite')}>فحص دعوة مكالمة</Button>
        </div>
      </Panel>
      <Panel title="صندوق المبادرات">
        {data && data.items.length ? <div className="card-list">{data.items.map((initiative) => <article key={initiative.id} className="initiative-card">
          <div className="row-between">
            <strong>{initiative.kind === 'call_invite' ? 'دعوة مكالمة' : 'رسالة'}</strong>
            <Status tone={initiativeTone(initiative.status)}>{initiative.status}</Status>
          </div>
          <p>{typeof initiative.content.text === 'string' ? initiative.content.text : '—'}</p>
          <p className="muted">السبب: {initiative.reason} — الموضوع: {initiative.topic}</p>
          <p className="muted">أُنشئت: {formatDate(initiative.createdAt)}{initiative.expiresAt ? ` — تنتهي: ${formatDate(initiative.expiresAt)}` : ''}</p>
          {initiative.kind === 'call_invite' && initiative.inviteId && (initiative.status === 'ready' || initiative.status === 'sent') ? <div className="inline-actions">
            <Button busy={busy} onClick={() => void accept(initiative)}>قبول المكالمة</Button>
            <Button variant="secondary" busy={busy} onClick={() => void reject(initiative)}>رفض</Button>
          </div> : null}
        </article>)}</div> : <Empty title="لا مبادرات بعد" />}
      </Panel>
    </div>}
  </>;
}


export function TrashPage() {
  const loader = useCallback(() => api.trash(), []);
  const { data, error, loading, reload } = useLoad(loader);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const scopeLabel = (scope: string): string => scope === 'memory' || scope === 'fact' ? 'ذكرى' : scope === 'message' ? 'رسالة' : scope === 'conversation' ? 'محادثة' : 'تسجيل';
  async function restore(item: TrashItem) {
    setBusy(item.requestId); setNotice('');
    try {
      const result = await api.restoreFromTrash(item.requestId);
      setNotice(result.keptHidden.length > 0
        ? 'استُرجعت العناصر الممكنة (' + String(result.restored) + ')، وبقيت ' + String(result.keptHidden.length) + ' محجوبة لأن المالك حذفها نهائيًا.'
        : 'استُرجعت ' + String(result.restored) + ' عنصرًا من السلة.');
      await reload();
    } catch (caught) { setNotice(errorMessage(caught)); } finally { setBusy(''); }
  }
  async function destroy(item: TrashItem) {
    if (!confirm('حذف نهائي لا يمكن التراجع عنه: ' + scopeLabel(item.scope) + ' (' + String(item.targetCount) + ' عنصرًا).\nسيُنشأ سجل منع استعادة بدون نص المحتوى. متابعة؟')) return;
    setBusy(item.requestId); setNotice('');
    try {
      const request = await api.permanentFromTrash(item.requestId);
      setNotice('اكتمل الحذف النهائي. ' + describeSteps(request));
      await reload();
    } catch (caught) { setNotice(errorMessage(caught)); } finally { setBusy(''); }
  }
  return <><PageTitle title="سلة المحذوفات">كل ما نُقل إلى السلة يبقى هنا حتى تقرر حذفه نهائيًا؛ لا حذف تلقائي بمهلة.</PageTitle>
    {error ? <ErrorNotice message={error} onRetry={() => void reload()} /> : null}
    {notice ? <div className="notice" role="status">{notice}</div> : null}
    <Panel title="عناصر السلة" intro="النوع والوقت والنطاق والحالة، وهل الاسترجاع ممكن.">
      {loading ? <Skeleton /> : data?.items.length ? <div className="card-list">{data.items.map((item) => <article key={item.requestId}>
        <div className="row-between"><strong>{scopeLabel(item.scope)} — {item.targetCount} عنصرًا</strong><Status tone={item.status === 'trashed' ? 'warning' : item.status === 'restored' ? 'good' : 'neutral'}>{statusLabel(item.status)}</Status></div>
        <details className="technical-details"><summary>تفاصيل تقنية</summary><p className="muted">المعرّف التقني للطلب: {item.requestId} — الأهداف: {item.targetIds.length}</p></details><p className="muted">نُقل في {formatDate(item.trashedAt ?? undefined)}{item.restoredAt ? ' — استُرجِع في ' + formatDate(item.restoredAt) : ''}</p>
        <p className="muted">{item.restorable ? 'الاسترجاع ممكن' : 'الاسترجاع غير ممكن: بعض العناصر حُذفت نهائيًا (' + String(item.blockedTargets) + ')'}</p>
        <div className="inline-actions">
          <Button variant="secondary" busy={busy === item.requestId} disabled={!item.restorable} onClick={() => void restore(item)}>استرجاع</Button>
          <Button variant="danger" busy={busy === item.requestId} disabled={item.status === 'restored' && item.blockedTargets >= item.targetCount} onClick={() => void destroy(item)}>حذف نهائي</Button>
        </div>
      </article>)}</div> : <Empty title="السلة فارغة" />}
    </Panel>
    <Panel title="كيف يعمل الحذف"><p>النقل إلى السلة يوقف الاستخدام فورًا ويُخفي العنصر من القوائم والبحث وسياق الذكاء، ويبقى قابلًا للاسترجاع.</p><p className="muted">الحذف النهائي يزيل المحتوى ومشتقاته الحصرية ويُنشئ سجل منع استعادة؛ وبايتات النسخ الاحتياطية المشفرة تنتهي وفق سياسة الاحتفاظ لا فورًا.</p></Panel>
  </>;
}
